import { Card, Check, cn } from "@app/ui";

import { useAppLocale } from "../../lib/useAppLocale.js";
import { useI18n } from "../../lib/useI18n.js";
import type { AgendaDay } from "./agenda.js";
import { agendaDayLabel, agendaDayNavigable } from "./agenda.js";

interface Props {
  days: AgendaDay[];
  now: Date;
  /** Scroll the i-th rendered day section into view. */
  onJump: (index: number) => void;
  /** Empty upcoming days have real sections only when this setting is on. */
  emptyDaysVisible?: boolean;
}

/**
 * Slim agenda-at-a-glance strip under the hero: one cell per agenda day with the
 * weekday, day number and its scheduled count (check when all done, faint dot when
 * free) — so "is Thursday free?" is answerable without scrolling. Clicking a
 * day scrolls to its section. Empty days are enabled only when the matching
 * "show empty days" setting has caused those sections to render.
 */
export function WeekStrip({ days, now, onJump, emptyDaysVisible = false }: Props) {
  const { t } = useI18n();
  const locale = useAppLocale();
  const relativeLabels = { today: t("today.relative.today"), tomorrow: t("today.relative.tomorrow") };

  return (
    <Card
      role="navigation"
      aria-label={t("today.weekOverview")}
      className="grid gap-1 p-1.5"
      // Follows the agendaDays setting: ≤7 days on one row, 14 wraps to two.
      style={{ gridTemplateColumns: `repeat(${Math.min(7, Math.max(1, days.length))}, minmax(0, 1fr))` }}
    >
      {days.map(({ date, items }, i) => {
        const scheduled = items.filter((x) => x.status !== "done").length;
        const empty = items.length === 0;
        const allDone = !empty && scheduled === 0;
        const isToday = i === 0;
        // Today always has a real section (including its empty state). Only
        // empty UPCOMING days disappear when the preference is off.
        const navigable = agendaDayNavigable(i, items.length, emptyDaysVisible);
        const label = agendaDayLabel(date, now, locale, relativeLabels);
        // Events are not completable but still occupy the day, so this count
        // describes scheduled items rather than calling an event "open".
        const state = empty ? t("today.dayFree") : allDone ? t("today.dayDone") : t("today.scheduledCount", { count: scheduled });
        return (
          <button
            key={date.toISOString()}
            type="button"
            disabled={!navigable}
            onClick={() => onJump(i)}
            title={`${label} — ${state}`}
            className={cn(
              "flex flex-col items-center gap-0.5 rounded-lg py-1.5 transition-colors",
              !navigable ? "cursor-default" : "hover:bg-accent/50",
              isToday && "bg-primary/10",
            )}
          >
            <span className="text-[calc(10px*var(--app-text-scale))] font-medium uppercase tracking-wide text-muted-foreground">
              {date.toLocaleDateString(locale, { weekday: "short" })}
            </span>
            <span className={cn("text-sm font-semibold leading-none tabular-nums", isToday && "text-primary")}>
              {date.getDate()}
            </span>
            <span className="grid h-4 place-items-center">
              {scheduled > 0 ? (
                <span className="min-w-4 rounded-full bg-primary/15 px-1 text-center text-[calc(10px*var(--app-text-scale))] font-semibold leading-4 tabular-nums text-primary">
                  {scheduled}
                </span>
              ) : allDone ? (
                <Check className="h-3 w-3 text-emerald-500" aria-hidden />
              ) : (
                <span className="h-1 w-1 rounded-full bg-muted-foreground/30" aria-hidden />
              )}
            </span>
          </button>
        );
      })}
    </Card>
  );
}
